/**
 * Dynamic Field Renderer Component
 * 
 * Schema-driven field rendering component that replaces hardcoded field types
 * with dynamic field generation based on backend schema definitions.
 * 
 * Evidence: Current renderField method is hardcoded (lines 1100-1394), 
 * needs schema-driven approach for conditional field support.
 */

import React from 'react';
import OmTextField from '../ui/OmTextField';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';
import OmDatePicker from '../ui/OmDatePicker';
import OmCheckbox from '../ui/OmCheckbox';

const DynamicFieldRenderer = ({ 
  fieldName, 
  fieldMetadata, 
  value, 
  onChange, 
  error, 
  disabled = false 
}) => {
  // DEBUG: Log rendering for parentRelationshipVerified
  if (fieldName === 'parentRelationshipVerified') {
    console.log('🐛 DEBUG DynamicFieldRenderer rendering parentRelationshipVerified:', {
      fieldName,
      fieldMetadata,
      value,
      hasOnChange: !!onChange,
      error,
      disabled
    });
  }
  
  // Return null if field should not be visible
  if (!fieldMetadata) return null;
  
  const {
    type,
    required,
    options = [],
    validation = {},
    label,
    helpText
  } = fieldMetadata;
  
  // Common props for all field types
  const commonProps = {
    name: fieldName,
    value: value || '',
    onChange: (e) => onChange(fieldName, e.target.value),
    required,
    errorMessage: error,
    disabled,
    label,
    helperText: helpText
  };
  
  // Render field based on schema-defined type
  switch (type) {
    case 'select':
      return (
        <OmSelect {...commonProps}>
          <OmSelectOption value="">Select {label.toLowerCase()}...</OmSelectOption>
          {options.map(option => (
            <OmSelectOption 
              key={option.value || option} 
              value={option.value || option}
            >
              {option.label || option}
            </OmSelectOption>
          ))}
        </OmSelect>
      );
      
    case 'date':
      return (
        <OmDatePicker
          {...commonProps}
          type="date"
        />
      );
      
    case 'number':
      return (
        <OmTextField
          {...commonProps}
          type="number"
          min={validation.min}
          max={validation.max}
          step={validation.step}
        />
      );
      
    case 'boolean':
      return (
        <OmCheckbox
          name={fieldName}
          checked={value === true || value === 'true'}
          onChange={(e) => {
            // DEBUG: Log onChange for parentRelationshipVerified
            if (fieldName === 'parentRelationshipVerified') {
              console.log('🐛 DEBUG DynamicFieldRenderer boolean onChange:', {
                fieldName,
                event: e,
                targetChecked: e.target.checked,
                beforeValue: value,
                newValue: e.target.checked
              });
            }
            onChange(fieldName, e.target.checked);
          }}
          required={required}
          errorMessage={error}
          disabled={disabled}
        >
          {label}
        </OmCheckbox>
      );
      
    case 'textarea':
      return (
        <OmTextField
          {...commonProps}
          type="textarea"
          rows={4}
          pattern={validation.pattern}
          maxLength={validation.maxLength}
        />
      );
      
    case 'text':
    default:
      return (
        <OmTextField
          {...commonProps}
          type="text"
          pattern={validation.pattern}
          maxLength={validation.maxLength}
        />
      );
  }
};

export default DynamicFieldRenderer;